import { IconSiren } from 'assets/icons';
import dayjs from 'dayjs';
import styled from 'styled-components';
import {
  FixedTicket,
  ItemState,
  ProductState,
  ScheduleState,
  TicketState,
  UnfixedTicket,
} from 'types/api';

const StatusBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;

  width: fit-content !important;
  min-height: 24px;
  max-height: 24px;

  padding: 0px 6px;
  border-radius: 6px;

  font-weight: 600;
  font-size: 11px;
  line-height: 24px;

  color: #0071e9;
  background-color: #d5e9ff;

  &.paused {
    color: rgba(221, 129, 0, 1);
    background-color: rgba(255, 229, 193, 1);
  }

  &.finished {
    color: #989ba2;
    background-color: #eaebec;
  }

  &.soldOut {
    color: #fff;
    background-color: rgba(65, 75, 86, 1);
  }

  &.selling {
    color: #414b56;
    background-color: #dce2e9;
  }

  &.canceled {
    color: rgba(255, 66, 66, 1);
    background-color: rgba(255, 230, 230, 1);
  }
`;

const SirenBadge = styled(StatusBadge)`
  border: 1px solid;
  border-color: rgba(255, 66, 66, 1);

  color: rgba(255, 66, 66, 1);
  background-color: #fff;
`;

const STATUS_TEXT = {
  paused: '일시중지', // both
  finished: '일정종료', // both
  scheduled: '진행예정', // both
  processing: '진행중', // both
  soldOut: '매진', // 지정형만
  selling: '판매중', // 지정형만
  canceled: '일정취소', // 지정형만
};

interface Props {
  item: UnfixedTicket | FixedTicket;
  hideCount?: boolean;
}

/* 지정형 */
const FixedTicketStatusBadge = ({
  item,
  hideCount,
}: {
  item: FixedTicket;
  hideCount?: boolean;
}) => {
  const isByItem = item.product.inventoryTargetType === 'BY_ITEM'; // 아이템 마다 재고 있음
  const isBySchedule = item.product.inventoryTargetType === 'BY_SCHEDULE'; // 총 재고만 있음
  const optionItemsList = item.items.filter(
    opt => opt.status === ItemState.OPENED,
  );

  const byItemMinimumQuota = optionItemsList.filter(
    opt => opt.minimumQuota > opt.counts.sale,
  );

  const isMinimumQuota = isByItem
    ? byItemMinimumQuota.length > 0
    : item.minimumQuota > item.allSalesCount?.sale;

  const isCanceled = item.status === ScheduleState.CANCELED;
  const pausedItem = item.status === ScheduleState.PAUSED;
  const finishedEventItem = item.status === ScheduleState.FINISHED;
  const isSoldOut = item.status === ScheduleState.SOLD_OUT;

  const isBeforeSellingPeriod = item.saleTerm.endedAt < dayjs().valueOf(); // 신청 마감일 지났을때

  const isInPeriod =
    item.term.startedAt <= dayjs().valueOf() &&
    dayjs().valueOf() <= item.term.endedAt;

  const isAfterSellingPeriod = dayjs().valueOf() > item.term.endedAt;

  const status = isCanceled
    ? 'canceled'
    : pausedItem
    ? 'paused'
    : finishedEventItem
    ? 'finished'
    : isSoldOut
    ? 'soldOut'
    : isAfterSellingPeriod
    ? 'finished'
    : isInPeriod
    ? 'processing'
    : isBeforeSellingPeriod
    ? 'scheduled'
    : 'selling';

  return (
    <>
      {isMinimumQuota && item.status !== ScheduleState.CANCELED && (
        <SirenBadge>
          <IconSiren width={12} height={12} />
          최소인원미달
        </SirenBadge>
      )}
      <StatusBadge className={status}>
        {`${STATUS_TEXT[status]} ${
          isCanceled || hideCount ? '' : item.allSalesCount?.sale
        }`}
      </StatusBadge>
    </>
  );
};

/* 조율형 */
const UnfixedTicketStatusBadge = ({
  item,
  hideCount,
}: {
  item: UnfixedTicket;
  hideCount?: boolean;
}) => {
  /* 조율형은 최소인원 없음 */

  const {
    absent: absentCounts,
    applied: appliedCounts,
    confirmed: confirmedCounts,
    total: totalCounts,
    used: usedCounts,
  } = item.bookingItem.counts;

  const pausedItem =
    item.bookingItem?.booking?.info?.origin?.product?.status === ProductState.PAUSED;

  const isCanceled = item.status === TicketState.CANCELED;

  const isBeforeSellingPeriod = item.scheduleTerm.startedAt < dayjs().valueOf(); // 신청 마감일 지났을때

  const isInPeriod =
    item.scheduleTerm.startedAt <= dayjs().valueOf() &&
    dayjs().valueOf() <= item.scheduleTerm.endedAt;
  const isAfterSellingPeriod = dayjs().valueOf() > item.scheduleTerm.endedAt;

  const status = isCanceled
    ? 'canceled'
    : pausedItem
    ? 'paused'
    : isAfterSellingPeriod
    ? 'finished'
    : isInPeriod
    ? 'processing'
    : // : isBeforeSellingPeriod
      // ? 'scheduled'
      'scheduled'; // 조율형 디폴트는 진행예정

  return (
    <StatusBadge className={status}>
      {`${STATUS_TEXT[status]} ${
        isCanceled || hideCount ? '' : item.exposedCounts
      }`}
    </StatusBadge>
  );
};

function ProductStatusBadge({ item, hideCount = false }: Props) {
  const isFixedTicket = 'minimumQuota' in item;

  return (
    <>
      {isFixedTicket ? (
        <FixedTicketStatusBadge item={item} hideCount={hideCount} />
      ) : (
        <UnfixedTicketStatusBadge item={item} hideCount={hideCount} />
      )}
    </>
  );
}

export default ProductStatusBadge;
